import { CheckoutProgress, CheckoutProgressStep } from "./interfaces";

type CheckoutMethod = CheckoutProgress["method"];

type VisualProgress = CheckoutProgress;

const STEP_LABELS: Record<Exclude<CheckoutProgressStep, "failed">, string> = {
  generating_pix: "Gerando cobrança PIX",
  generating_link: "Gerando link de pagamento",
  waiting_payment: "Aguardando pagamento",
  registering_payment: "Registrando pagamento",
  payment_registered: "Pagamento registrado",
  confirming_order: "Confirmando pedido",
  order_confirmed: "Pedido confirmado",
  finalizing_order: "Finalizando pedido",
  completed: "Pedido finalizado",
};

const STEP_DESCRIPTIONS: Record<Exclude<CheckoutProgressStep, "failed">, string> = {
  generating_pix: "Estamos gerando o QR Code e preparando a cobrança para pagamento.",
  generating_link: "Estamos preparando o link para que o pagamento possa ser realizado.",
  waiting_payment: "Estamos aguardando a confirmação do pagamento.",
  registering_payment: "Estamos registrando o pagamento.",
  payment_registered: "Pagamento registrado com sucesso. Agora vamos confirmar o pedido.",
  confirming_order: "Estamos confirmando as poltronas com o fornecedor.",
  order_confirmed: "Pedido confirmado. Agora vamos concluir a emissão.",
  finalizing_order: "Estamos emitindo os documentos e concluindo a compra.",
  completed: "Pedido concluído com sucesso.",
};

const STEP_RANK: Record<CheckoutProgressStep, number> = {
  generating_pix: 0,
  generating_link: 0,
  waiting_payment: 1,
  registering_payment: 2,
  payment_registered: 3,
  confirming_order: 4,
  order_confirmed: 5,
  finalizing_order: 6,
  completed: 7,
  failed: -1,
};

const SEQUENCES: Record<CheckoutMethod, CheckoutProgressStep[]> = {
  cartao: [
    "waiting_payment",
    "registering_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
  dinheiro: [
    "waiting_payment",
    "registering_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
  pix: [
    "generating_pix",
    "waiting_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
  link: [
    "generating_link",
    "waiting_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
  pos: [
    "waiting_payment",
    "registering_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
  desconhecido: [
    "waiting_payment",
    "registering_payment",
    "payment_registered",
    "confirming_order",
    "order_confirmed",
    "finalizing_order",
    "completed",
  ],
};

export function createCheckoutProgressOverride(
  pedidoId: string | number,
  method: CheckoutMethod,
  step: CheckoutProgressStep,
  label: string,
  description: string,
): VisualProgress {
  return {
    pedido_id: Number(pedidoId),
    step,
    label,
    description,
    status: "in_progress",
    method,
    updated_at: new Date().toISOString(),
    error_message: null,
    redirect_to: null,
  };
}

export function mergeCheckoutProgress(
  serverProgress?: CheckoutProgress,
  localProgress?: VisualProgress | null,
): CheckoutProgress | undefined {
  if (!localProgress) {
    return serverProgress;
  }

  if (!serverProgress) {
    return localProgress;
  }

  if (serverProgress.status === "failed" || serverProgress.status === "completed") {
    return serverProgress;
  }

  if (serverProgress.method !== localProgress.method) {
    return localProgress;
  }

  const localRank = STEP_RANK[localProgress.step];
  const serverRank = STEP_RANK[serverProgress.step];

  if (serverRank > localRank) {
    return serverProgress;
  }

  if (localRank > serverRank) {
    return localProgress;
  }

  if (localRank === serverRank) {
    return localProgress;
  }

  return serverProgress;
}

export function createCheckoutProgressFromStep(
  pedidoId: string | number,
  method: CheckoutMethod,
  step: Exclude<CheckoutProgressStep, "failed">,
  overrides?: Partial<CheckoutProgress>,
): CheckoutProgress {
  return {
    pedido_id: Number(pedidoId),
    step,
    label: STEP_LABELS[step],
    description: STEP_DESCRIPTIONS[step],
    status: step === "completed" ? "completed" : "in_progress",
    method,
    updated_at: new Date().toISOString(),
    error_message: null,
    redirect_to: null,
    ...overrides,
  };
}

export function getCheckoutProgressRank(step: CheckoutProgressStep): number {
  return STEP_RANK[step];
}

export function getCheckoutProgressSteps(method: CheckoutMethod): Array<{ key: CheckoutProgressStep; label: string }> {
  const sequence = SEQUENCES[method] ?? SEQUENCES.desconhecido;

  return sequence.map((step) => ({
    key: step,
    label: STEP_LABELS[step as Exclude<CheckoutProgressStep, "failed">] ?? step,
  }));
}

export function getCheckoutProgressIndex(method: CheckoutMethod, step: CheckoutProgressStep): number {
  if (step === "failed") {
    return 0;
  }

  const sequence = SEQUENCES[method] ?? SEQUENCES.desconhecido;
  const foundIndex = sequence.indexOf(step);

  if (foundIndex >= 0) {
    return foundIndex;
  }

  if (step === "completed") {
    return sequence.length - 1;
  }

  return 0;
}

export function getCheckoutProgressTransitionQueue(
  method: CheckoutMethod,
  fromStep: CheckoutProgressStep,
  toStep: CheckoutProgressStep,
): CheckoutProgressStep[] {
  const sequence = SEQUENCES[method] ?? SEQUENCES.desconhecido;
  const fromRank = STEP_RANK[fromStep];
  const toRank = STEP_RANK[toStep];

  if (toRank <= fromRank) {
    return [];
  }

  return sequence.filter((step) => {
    const rank = STEP_RANK[step];

    return rank > fromRank && rank <= toRank;
  });
}
